/**
 * Address validation utilities
 */

// Define and export Postal Code Formats
export const postalCodeFormats: {
  [key: string]: { regex: RegExp; example: string };
} = {
  USA: { regex: /^\d{5}(-\d{4})?$/, example: '12345 or 12345-6789' },
  US: { regex: /^\d{5}(-\d{4})?$/, example: '12345 or 12345-6789' }, // Add US as alias for USA
  Belgium: { regex: /^\d{4}$/, example: '1000' },
  Canada: {
    regex: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    example: 'A1A 1A1',
  },
  'United Kingdom': {
    regex: /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/i,
    example: 'SW1A 1AA',
  },
  Australia: { regex: /^\d{4}$/, example: '2000' },
  'New Zealand': { regex: /^\d{4}$/, example: '1010' },
  Luxembourg: { regex: /^L-\d{4}$/i, example: 'L-1234' },
  Denmark: { regex: /^\d{4}$/, example: '1000' },
  Switzerland: { regex: /^\d{4}$/, example: '8001' },
  Singapore: { regex: /^\d{6}$/, example: '238859' },
};

/**
 * Validates a postal code string against known formats for specific countries.
 *
 * @param postalCode Postal code string to validate
 * @param country Country code to determine validation rules
 * @returns boolean indicating if the postal code is valid for the given country format
 */
// Map of country codes to full names
export const countryCodeMap: { [key: string]: string } = {
  US: 'USA',
  USA: 'USA',
  BE: 'Belgium',
  CA: 'Canada',
  GB: 'United Kingdom',
  UK: 'United Kingdom',
  AU: 'Australia',
  NZ: 'New Zealand',
  LU: 'Luxembourg',
  DK: 'Denmark',
  CH: 'Switzerland',
  SG: 'Singapore',
};

export const validatePostalCode = (
  postalCode: string | undefined | null,
  country?: string
): boolean => {
  if (!postalCode) return false;

  const trimmedPostalCode = postalCode.trim();
  if (!trimmedPostalCode) return false;

  // Map country code to full name if needed
  const countryName = country ? countryCodeMap[country] || country : null;

  // Find the format for the specified country
  const formatInfo = countryName ? postalCodeFormats[countryName] : null;

  if (formatInfo) {
    // Validate against the specific country's regex
    return formatInfo.regex.test(trimmedPostalCode);
  } else {
    // If no country specified or country not found in formats, default to USA format
    // This ensures backward compatibility with existing code that doesn't pass a country
    return postalCodeFormats.USA.regex.test(trimmedPostalCode);
  }
};

export default {
  validatePostalCode,
  postalCodeFormats, // Also export formats in the default export if desired
};
